import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowRightCircle, ArrowUp, ArrowUpDown, ChevronRight, Circle, Clock3, FileCheck2, Loader2, RefreshCw, RotateCcw, Search, Link2, FilePlus, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { apiGet, apiPost, fmtDate } from "@/utils/api";

/* ────────────────────────────────────────────────────────────────────
 * SAP Fiori / ByDesign-style register
 *   • Row height 32-36px, 13px font, 8-12px cell padding
 *   • Grey column headers (#F5F6F7), uppercase 11px, no colored row
 *     tints — plain white with #FAFAFA zebra, hover #EAEFF2
 *   • Selection: left-border 3px solid SAP blue (#0854A0) + bg #DAE9F8
 *   • Status pills (not colored blocks)
 *   • Flat compact toolbar with SAP primary blue CTA
 * ──────────────────────────────────────────────────────────────────── */

const SAP_BLUE = "#0854A0";
const SAP_BLUE_HOVER = "#064378";
const SAP_SELECTED_BG = "#DAE9F8";

const receivedLabel = (item) => {
  const breakdown = item.received_by_uom || {};
  const parts = Object.entries(breakdown).filter(([uom]) => uom).map(([uom, quantity]) => `${quantity} ${uom}`);
  return parts.length ? parts.join(" · ") : `${item.received_quantity} ${(item.uoms || []).join(", ")}`;
};

const grnLabel = (item) => {
  const ids = item.grn_ids && item.grn_ids.length ? item.grn_ids : (item.grn_id ? [item.grn_id] : []);
  if (!ids.length) return "—";
  return ids.length > 1 ? `${ids.length} GRNs · ${ids.join(", ")}` : ids[0];
};

const notificationLabel = (item) => {
  const ids = item.notification_ids && item.notification_ids.length ? item.notification_ids : (item.notification_id ? [item.notification_id] : []);
  if (!ids.length) return "—";
  return ids.length > 1 ? ids.join(" · ") : ids[0];
};

const buildHaystack = (item) => {
  const parts = [
    item.worklist_id, item.shipment_code, item.vendor_id, item.vendor_name, item.site,
    item.supplier_invoice_number, item.materials_hub_supplier_invoice_number,
    ...(item.notification_ids || [item.notification_id]).filter(Boolean),
    ...(item.grn_ids || [item.grn_id]).filter(Boolean),
    ...(item.po_numbers || []),
    ...(item.po_items || []).flatMap((p) => [p.po_number, p.po_item_number, p.material_code]),
  ];
  return parts.filter(Boolean).map(String).join(" \u2003 ").toLowerCase();
};

const matchesSearch = (item, query) => {
  if (!query) return true;
  const haystack = buildHaystack(item);
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((token) => haystack.includes(token));
};

/* ── Sorting ────────────────────────────────────────────────────────
 * SAP ByD convention: click a column header to sort asc, click again
 * to flip desc. Default sort is by SAP-received date desc so the
 * newest GRNs float to the top.
 */
const LIFECYCLE_SORT_ORDER = { exception: 0, blocked: 1, in_process: 2, not_started: 3, posted: 4 };

function lifecycleKey(item) {
  const exceptionOpen = item.status === "three_way_exception" && item.three_way_exception_id;
  if (exceptionOpen) return "exception";
  if (item.blocked_by_earlier_grn) return "blocked";
  // Two backend conventions both mean "posted" (server.py): the in-app
  // Post flow (_run_finalize_sap_post) writes status="invoice_posted" /
  // status_label="Posted"; the background SAP reconciliation probe writes
  // status="fully_invoiced" / status_label="Posted in SAP". Only the
  // second was recognized here, so invoices posted the normal way kept
  // showing in the default (non-posted) view instead of being hidden.
  const posted = (item.sap_invoice_refs || []).some((r) => r.status_label === "Posted in SAP" || r.status_label === "Posted")
    || item.status === "fully_invoiced" || item.status === "invoice_posted";
  if (posted) return "posted";
  const sapDraft = (item.sap_invoice_refs || []).length > 0 || (item.supplier_invoice_ids || []).length > 0;
  const localDraft = item.status === "draft_in_progress" || item.status === "match_approved";
  if (sapDraft || localDraft) return "in_process";
  return "not_started";
}

function sortValue(item, key) {
  switch (key) {
    case "shipment":    return (item.shipment_code || "").toLowerCase();
    case "supplier":    return (item.vendor_name || "").toLowerCase();
    case "notification":return (item.notification_ids?.[0] || item.notification_id || "").toLowerCase();
    case "po":          return (item.po_items?.[0]?.po_number || item.po_numbers?.[0] || "").toLowerCase();
    case "material":    return (item.po_items?.[0]?.material_code || "").toLowerCase();
    case "received":    return Number(item.received_quantity || 0);
    case "status":      return LIFECYCLE_SORT_ORDER[lifecycleKey(item)] ?? 99;
    case "site":        return (item.site || "").toLowerCase();
    case "date":
    default: {
      const ts = item.materials_hub_grn_created_at || item.source_updated_at || item.reconciled_at || "";
      return ts;
    }
  }
}

function compareBy(key, dir) {
  const sign = dir === "desc" ? -1 : 1;
  return (a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (va < vb) return -1 * sign;
    if (va > vb) return 1 * sign;
    // tie-break on shipment to keep the order deterministic across
    // re-renders (otherwise React would reshuffle equal rows on every
    // state tick, which looks like a bug).
    const sa = (a.shipment_code || "").toLowerCase();
    const sb = (b.shipment_code || "").toLowerCase();
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  };
}

const toolbarEligibility = (item) => {
  if (!item) return { eligible: false };
  const alreadyInSap = item.status === "fully_invoiced";
  const draft = item.status === "draft_in_progress";
  const exceptionOpen = item.status === "three_way_exception" && item.three_way_exception_id;
  const blockedByEarlierGrn = Boolean(item.blocked_by_earlier_grn);
  const approvedMatch = item.status === "match_approved" && item.three_way_match_id;
  if (alreadyInSap || draft || exceptionOpen || blockedByEarlierGrn) {
    return { eligible: false, reason:
      alreadyInSap ? "Already in SAP"
      : exceptionOpen ? "Open the exception from its row"
      : blockedByEarlierGrn ? "Clear the earlier open GRN first"
      : "A local draft already exists — use the row action to continue",
    };
  }
  if (approvedMatch) return { eligible: true, mode: "continue" };
  return { eligible: true, mode: "prepare" };
};

const buildCreateInvoiceUrl = (item) => {
  const { mode } = toolbarEligibility(item);
  if (mode === "prepare") {
    // Invoice creation is always shipment-code based (Oct 2026): one
    // Materials Hub shipment is always invoiced as one atomic whole, even
    // when it spans multiple POs and SAP/Materials Hub splits it into
    // several GRNs/inbound-delivery IDs. The per-worklist_id (single-GRN)
    // route is retired as the entry point.
    const previewSuffix = item.materials_hub === "preview" ? `?${new URLSearchParams({ hub: "preview" })}` : "";
    return `/invoices/prepare/direct-${encodeURIComponent(item.shipment_code)}${previewSuffix}`;
  }
  if (mode === "continue") {
    // A shipment is always matched/approved as one atomic whole now (Oct
    // 2026), so continuing always resumes via the shipment-level reference,
    // never a single underlying GRN's notification_id/worklist_id. It still
    // routes through the tax-setup wizard so the Sec 194Q / GST checks are
    // never silently bypassed on a resumed invoice.
    const worklistRef = `direct-${item.shipment_code}`;
    const params = { source: "shipment", reference: item.shipment_code, worklist: worklistRef, match: item.three_way_match_id, prepare: "1" };
    if (item.materials_hub === "preview") params.hub = "preview";
    return `/invoices/tax-setup/${encodeURIComponent(worklistRef)}?${new URLSearchParams(params)}`;
  }
  return null;
};

const openCreateInvoiceTab = (item) => {
  const url = buildCreateInvoiceUrl(item);
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

function SortableHeader({ label, sortKey: colKey, activeKey, dir, onSort, align = "left" }) {
  const active = activeKey === colKey;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return <th className={`px-4 py-3 font-bold ${align === "right" ? "text-right" : ""}`}>
    <button
      type="button"
      onClick={() => onSort(colKey)}
      className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-900 ${active ? "text-slate-900" : "text-slate-600"}`}
      data-testid={`sort-header-${colKey}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      <Icon size={11} className={active ? "" : "opacity-40"} />
    </button>
  </th>;
}

/* ── Compact status pills (SAP Fiori "object status" semantic) ─────── */

const LIFECYCLE_PALETTE = {
  not_started: { tone: "neutral", label: "Not started", icon: Circle },
  in_process:  { tone: "info",    label: "In process",  icon: Loader2 },
  posted:      { tone: "success", label: "Posted",      icon: CheckCircle2 },
  blocked:     { tone: "warning", label: "Blocked",     icon: AlertTriangle },
  exception:   { tone: "error",   label: "Exception",   icon: AlertTriangle },
  cancelled:   { tone: "neutral", label: "Cancelled in SAP", icon: XCircle },
};
const STATUS_FILTER_OPTIONS = ["not_started", "in_process", "blocked", "exception", "posted"];

// Collapse the mix of ``item.status`` (local + reconciled), ``sap_invoice_refs``,
// ``local_draft_status`` and the FIFO ``blocked_by_earlier_grn`` into a single
// user-facing lifecycle state. The order matters — Exception / Blocked take
// precedence over SAP state so users see the thing they must act on first.
function resolveLifecycle(item) {
  const exceptionOpen = item.status === "three_way_exception" && item.three_way_exception_id;
  if (exceptionOpen) return LIFECYCLE_PALETTE.exception;
  if (item.status === "cancelled_in_sap") return LIFECYCLE_PALETTE.cancelled;
  if (item.blocked_by_earlier_grn) return LIFECYCLE_PALETTE.blocked;
  const sapRefs = item.sap_invoice_refs || [];
  const posted = sapRefs.some((reference) => reference.status_label === "Posted in SAP" || reference.status_label === "Posted")
    || item.status === "fully_invoiced" || item.status === "invoice_posted";
  if (posted) return LIFECYCLE_PALETTE.posted;
  const sapDraft = sapRefs.length > 0 || (item.supplier_invoice_ids || []).length > 0;
  const localDraft = item.status === "draft_in_progress" || item.status === "match_approved";
  if (sapDraft || localDraft) return LIFECYCLE_PALETTE.in_process;
  return LIFECYCLE_PALETTE.not_started;
}

function StatusPill({ tone = "neutral", icon: Icon, label, testId }) {
  const palette = {
    neutral:  { color: "#4A5765" },
    success:  { color: "#107E3E" },
    warning:  { color: "#B16D00" },
    error:    { color: "#BB0000" },
    info:     { color: "#0854A0" },
  };
  const p = palette[tone] || palette.neutral;
  return <span
    className="inline-flex items-center gap-1 text-[11px] font-medium whitespace-nowrap"
    style={{ color: p.color }}
    data-testid={testId}
  >
    {Icon ? <Icon size={11} /> : null}{label}
  </span>;
}

export const DueInvoicingWorklist = ({ onPrepare, onContinue }) => {
  void onPrepare; void onContinue;

  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [supplier, setSupplier] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hub, setHub] = useState("prod");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [sync, setSync] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [dueCount, setDueCount] = useState(0);
  const [sapInvoicedCount, setSapInvoicedCount] = useState(0);
  // Raw (pre-grouping) row count already fetched — the `skip` cursor for
  // Load More must track this, not `items.length`, since multiple raw
  // worklist rows (e.g. several GRNs on one shipment) can collapse into a
  // single displayed row.
  const [rawSkip, setRawSkip] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [restartMessage, setRestartMessage] = useState("");
  const [restartingId, setRestartingId] = useState("");
  const [restartCandidate, setRestartCandidate] = useState(null);
  const [recoveringId, setRecoveringId] = useState("");
  const [recoveryCandidate, setRecoveryCandidate] = useState(null);
  const [resyncingId, setResyncingId] = useState("");
  const [linkingId, setLinkingId] = useState("");
  const [linkCandidate, setLinkCandidate] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = useCallback((key) => {
    // Avoid a nested ``setSortDir`` inside a ``setSortKey`` updater —
    // React 18 StrictMode double-invokes updater functions for purity
    // checking, which would flip the direction twice per click.
    setSortKey((prevKey) => {
      if (prevKey === key) return prevKey;
      return key;
    });
    setSortDir((prevDir) => {
      // Dir resolution uses the committed sortKey value via a flushSync-
      // free approach: we read the latest sortKey through this hook's
      // closure (useCallback dependency), falling back to the key the
      // user just clicked when they switch columns.
      // When clicking the same key: flip the direction.
      // When clicking a different key: reset based on the key type.
      return key === sortKey
        ? (prevDir === "asc" ? "desc" : "asc")
        : (["received", "date"].includes(key) ? "desc" : "asc");
    });
  }, [sortKey]);

  // Debounce the free-text search before hitting the server. Without this,
  // the table only ever held the first page (most-recently-updated 100 rows)
  // and search only filtered *that* page client-side — any older row (e.g. a
  // shipment fully invoiced weeks ago) was invisible no matter how precisely
  // it was typed, since it was never fetched. Searching the server removes
  // that pagination blind spot.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // "Pending" (not-yet-posted) rows are the real backlog — every one of
  // them still has to be finished, so they are always fetched in full,
  // never truncated by a page. "Posted" history has no urgency, so it is
  // the only bucket that stays paginated (Load more fetches the next
  // page server-side — both are small, indexed fetches with no
  // perceptible delay).
  const scope = statusFilter === "posted" ? "posted" : "pending";
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (supplier !== "all") params.set("supplier", supplier);
      if (hub !== "prod") params.set("hub", hub);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("scope", scope);
      const response = await apiGet(`/invoice-worklist?${params}`);
      setItems(response.items || []); setSuppliers(response.suppliers || []); setSync(response.sync || null); setTotal(response.total || 0);
      setHasMore(Boolean(response.has_more)); setDueCount(response.due_count || 0); setSapInvoicedCount(response.sap_invoiced_count || 0);
      setRawSkip(response.fetched_count || 0);
    } catch (err) { setError(err.message || "Could not load due receipts"); }
    finally { setLoading(false); }
  }, [supplier, hub, debouncedSearch, scope]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (supplier !== "all") params.set("supplier", supplier);
      if (hub !== "prod") params.set("hub", hub);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("scope", scope);
      params.set("skip", String(rawSkip));
      const response = await apiGet(`/invoice-worklist?${params}`);
      setItems((prev) => [...prev, ...(response.items || [])]);
      setHasMore(Boolean(response.has_more)); setTotal(response.total || 0);
      setRawSkip((prev) => prev + (response.fetched_count || 0));
    } catch (err) { setError(err.message || "Could not load more rows"); }
    finally { setLoadingMore(false); }
  }, [supplier, hub, debouncedSearch, scope, rawSkip, loadingMore, hasMore]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (sync?.status !== "running") return undefined;
    const timer = setTimeout(load, 2500);
    return () => clearTimeout(timer);
  }, [sync?.status, load]);
  // A supplier filter OR a free-text search switches to a full pendency
  // view (including blocked GRNs the server now includes for either),
  // where the whole point is working the backlog in FIFO order — default
  // to oldest first so the correct next-to-invoice row reads top to
  // bottom instead of scattered by last-updated time. Clearing both
  // returns to the usual newest-first browsing order.
  useEffect(() => {
    const pendencyView = supplier !== "all" || Boolean(debouncedSearch);
    setSortKey("date");
    setSortDir(pendencyView ? "asc" : "desc");
  }, [supplier, debouncedSearch]);
  const refreshFromSources = async () => {
    setSyncing(true); setError("");
    try {
      await load();
      apiPost("/invoice-worklist/sync", {}).catch(() => null);
    }
    catch (err) { setError(err.message || "Could not load the current receipt list"); }
    finally { setSyncing(false); }
  };
  const restartDraft = async (item) => {
    setRestartingId(item.worklist_id); setError(""); setRestartMessage("");
    try {
      const result = await apiPost(`/invoice-worklist/restart?worklist_id=${encodeURIComponent(item.worklist_id)}`, {});
      setRestartMessage(result.message || "The local draft was restarted. Create a fresh three-way match.");
      await load();
    } catch (err) { setError(err.message || "Could not restart this local draft"); }
    finally { setRestartingId(""); }
  };
  const recoverDraft = async (item) => {
    setRecoveringId(item.worklist_id); setError(""); setRestartMessage("");
    try {
      const isUnknownRemoteResult = item.local_draft_status === "sap_draft_reconciliation_required";
      const path = isUnknownRemoteResult
        ? `/invoices/${encodeURIComponent(item.invoice_id)}/reconcile-sap-draft`
        : `/invoices/${encodeURIComponent(item.invoice_id)}/recover-sap-draft`;
      const result = await apiPost(path, {});
      setRestartMessage(isUnknownRemoteResult
        ? `Located ${result.sap_invoice_id} through SAP read-only reconciliation. No new SAP draft was created.`
        : `Recovered ${result.sap_invoice_id} for finance review. No new SAP draft was created.`);
      await load();
    } catch (err) { setError(err.message || "Could not recover the existing SAP draft"); }
    finally { setRecoveringId(""); }
  };
  const resyncSap = async (item) => {
    setResyncingId(item.worklist_id); setError(""); setRestartMessage("");
    try {
      const result = await apiPost(`/invoice-worklist/resync-sap?worklist_id=${encodeURIComponent(item.worklist_id)}`, {});
      const closed = (result.results || []).filter((row) => row.status === "fully_invoiced").length;
      const cleared = (result.results || []).filter((row) => row.status === "due_for_invoicing").length;
      const cancelled = (result.results || []).filter((row) => row.status === "cancelled_in_sap").length;
      setRestartMessage(`SAP re-checked ${(result.results || []).length} GRN(s). ${closed} already in SAP, ${cleared} still due${cancelled ? `, ${cancelled} found cancelled in SAP (will never be invoiced)` : ""}.`);
      await load();
    } catch (err) { setError(err.message || "SAP re-check failed"); }
    finally { setResyncingId(""); }
  };
  const linkSapPostedAndClose = async (item) => {
    setLinkingId(item.worklist_id); setError(""); setRestartMessage("");
    try {
      const result = await apiPost(`/invoice-worklist/link-sap-posted-and-close?worklist_id=${encodeURIComponent(item.worklist_id)}`, {});
      const linked = (result.linked_sap_invoices || []).join(", ");
      const skipped = (result.skipped_rows || []).length;
      if ((result.closed_rows || []).length === 0) {
        setError(skipped ? "SAP has no active item-level invoice for these GRNs — nothing to link." : "No worklist rows were closed.");
      } else {
        setRestartMessage(`Linked ${linked} and closed ${result.closed_rows.length} worklist row(s). ${skipped ? `${skipped} skipped (no SAP match).` : ""}`);
      }
      await load();
    } catch (err) { setError(err.message || "Could not link the posted SAP invoice"); }
    finally { setLinkingId(""); }
  };

  const filteredItems = useMemo(() => {
    // Posted (fully invoiced) shipments are hidden by default -- "All" here
    // means "all open work", not literally every row. Select the "Posted"
    // filter explicitly to see them.
    const matched = items.filter((item) => matchesSearch(item, search) && (statusFilter === "all" ? lifecycleKey(item) !== "posted" : lifecycleKey(item) === statusFilter));
    return [...matched].sort(compareBy(sortKey, sortDir));
  }, [items, search, statusFilter, sortKey, sortDir]);
  const selectedItem = useMemo(() => filteredItems.find((item) => item.worklist_id === selectedId) || null, [filteredItems, selectedId]);
  const toolbar = toolbarEligibility(selectedItem);

  useEffect(() => {
    if (selectedId && !filteredItems.some((item) => item.worklist_id === selectedId)) {
      setSelectedId("");
    }
  }, [filteredItems, selectedId]);

  // dueCount / sapInvoicedCount come from the server (set in `load`) — real
  // counts across the whole backlog, independent of how many rows are
  // currently loaded into `items` for the active scope.
  const createInvoiceButtonLabel = toolbar.mode === "continue" ? "Continue Invoice" : "Create Invoice";
  const createInvoiceButtonTitle = toolbar.eligible
    ? "Open the invoice workspace in a new tab (double-click a row for a shortcut)"
    : selectedItem ? toolbar.reason : "Select an eligible row from the table to begin";

  return <section className="bg-white" data-testid="due-invoicing-worklist">
    {/* ── SAP Fiori header bar ── */}
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-[#F7F7F7] px-4 py-2.5">
      <div className="flex items-baseline gap-2">
        <h2 className="text-[14px] font-semibold text-slate-900" data-testid="due-invoicing-heading">Due for Invoicing</h2>
        <span className="text-[12px] text-slate-500" data-testid="due-invoicing-total-count">({filteredItems.length}{total && filteredItems.length !== total ? ` of ${total}` : ""})</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={refreshFromSources}
          disabled={loading || syncing}
          className="h-8 gap-1.5 border-slate-300 bg-white text-[12px] font-normal text-slate-700 hover:bg-slate-50"
          data-testid="due-invoicing-refresh-button"
        ><RefreshCw size={13} className={syncing ? "animate-spin" : ""} />{syncing ? "Syncing" : "Refresh"}</Button>
        <Button
          onClick={() => selectedItem && openCreateInvoiceTab(selectedItem)}
          disabled={!toolbar.eligible}
          title={createInvoiceButtonTitle}
          className="h-8 gap-1.5 text-[12px] font-medium text-white disabled:cursor-not-allowed"
          style={{
            backgroundColor: toolbar.eligible ? SAP_BLUE : "#C5C9CE",
          }}
          onMouseEnter={(event) => { if (toolbar.eligible) event.currentTarget.style.backgroundColor = SAP_BLUE_HOVER; }}
          onMouseLeave={(event) => { if (toolbar.eligible) event.currentTarget.style.backgroundColor = SAP_BLUE; }}
          data-testid="create-invoice-toolbar-button"
        ><FilePlus size={13} />{createInvoiceButtonLabel}</Button>
      </div>
    </header>

    {/* ── Filter / search bar (compact, flat) ── */}
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2" data-testid="due-invoicing-search-controls">
      <div className="relative min-w-[260px] flex-1">
        <label className="sr-only" htmlFor="due-invoicing-search">Search all columns</label>
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          id="due-invoicing-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search shipment, GRN, PO, material, supplier, notification…"
          className="h-8 border-slate-300 bg-white pl-8 text-[12px] placeholder:text-slate-400"
          data-testid="due-invoicing-search-input"
          autoComplete="off"
        />
      </div>
      <Select value={supplier} onValueChange={setSupplier}>
        <SelectTrigger className="h-8 w-[200px] border-slate-300 bg-white text-[12px]" data-testid="due-invoicing-supplier-filter">
          <SelectValue placeholder="All suppliers" />
        </SelectTrigger>
        <SelectContent><SelectItem value="all" data-testid="due-supplier-all">All suppliers</SelectItem>{suppliers.map((name) => <SelectItem key={name} value={name} data-testid={`due-supplier-${name}`}>{name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="h-8 w-[160px] border-slate-300 bg-white text-[12px]" data-testid="due-invoicing-status-filter">
          <SelectValue placeholder="All statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="due-status-all">All (Posted hidden)</SelectItem>
          {STATUS_FILTER_OPTIONS.map((key) => <SelectItem key={key} value={key} data-testid={`due-status-${key}`}>{LIFECYCLE_PALETTE[key].label}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="inline-flex h-8 overflow-hidden rounded border border-slate-300" role="group" aria-label="Materials Hub environment" data-testid="due-invoicing-hub-toggle">
        {[['prod', 'Prod'], ['preview', 'Preview']].map(([key, label]) => <button type="button" key={key} aria-pressed={hub === key}
          onClick={() => setHub(key)} data-testid={`due-invoicing-hub-${key}-option`}
          className={`px-3 text-[12px] font-medium transition-colors ${hub === key ? "bg-slate-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{label}</button>)}
      </div>
      <div className="ml-auto hidden items-center gap-x-3 text-[11px] text-slate-500 md:flex" data-testid="due-invoicing-worklist-summary">
        <span data-testid="due-invoicing-eligible-count">{dueCount} eligible</span>
        <span data-testid="due-invoicing-sap-invoiced-count">{sapInvoicedCount} in SAP</span>
        {sync?.completed_at && <span className="inline-flex items-center gap-1" data-testid="due-invoicing-last-sync"><Clock3 size={11} />{new Date(sync.completed_at).toLocaleTimeString()}</span>}
      </div>
    </div>

    {/* ── Selection strip (SAP: thin, left-border info line) ── */}
    <div
      className="flex flex-wrap items-center gap-x-3 border-b bg-white px-4 py-1.5 text-[11px]"
      style={{ borderLeftColor: selectedItem ? SAP_BLUE : "transparent", borderLeftWidth: 3, borderBottomColor: "#E5E7EB" }}
      data-testid="due-invoicing-selection-strip"
    >
      {selectedItem ? <>
        <span className="font-medium text-slate-800" data-testid="selection-strip-shipment">{selectedItem.shipment_code || "—"}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600" data-testid="selection-strip-supplier">{selectedItem.vendor_name || "—"}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600" data-testid="selection-strip-grn">GRN {grnLabel(selectedItem)}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600" data-testid="selection-strip-po">PO {(selectedItem.po_numbers || []).join(", ") || "—"}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-600" data-testid="selection-strip-site">Site {selectedItem.site || "—"}{selectedItem.buyer_entity_name && <span className="text-slate-400"> ({selectedItem.buyer_entity_name})</span>}</span>
        {!toolbar.eligible && toolbar.reason && <>
          <span className="text-slate-400">·</span>
          <span style={{ color: "#B16D00" }} data-testid="selection-strip-reason">{toolbar.reason}</span>
        </>}
      </> : <span className="text-slate-400" data-testid="selection-strip-placeholder">No row selected</span>}
    </div>

    {hub === "preview" && <p className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-[11px] text-amber-900" data-testid="due-invoicing-preview-notice">Shipment lookup is targeting the Materials Hub <strong>preview</strong> environment. The periodic feed still runs against production.</p>}
    {error && <p role="alert" className="border-b border-red-200 bg-red-50 px-4 py-1.5 text-[11px] text-red-800" data-testid="due-invoicing-error">{error}</p>}
    {restartMessage && <p role="status" className="border-b border-emerald-200 bg-emerald-50 px-4 py-1.5 text-[11px] text-emerald-900" data-testid="due-invoicing-restart-success">{restartMessage}</p>}

    {loading ? (
      <div className="flex h-28 items-center justify-center" data-testid="due-invoicing-loading">
        <Loader2 className="animate-spin" size={16} style={{ color: SAP_BLUE }} />
      </div>
    ) : filteredItems.length === 0 ? (
      <div className="flex min-h-24 items-center justify-center bg-white px-4 py-8 text-center text-[12px] text-slate-500" data-testid="due-invoicing-empty">
        {items.length === 0 ? "No eligible SAP receipts are currently due for supplier invoicing." : "No rows match your search."}
      </div>
    ) : <>
      {/* Desktop: SAP-style table */}
      <div className="hidden overflow-x-auto bg-white lg:block" data-testid="due-invoicing-table">
        <table className="w-full border-collapse text-left text-[13px]">
          <thead>
            <tr className="border-b-2 border-slate-300 bg-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-700">
              <SortableHeader label="Shipment" sortKey="shipment" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Supplier" sortKey="supplier" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="SAP Notification / GRN" sortKey="notification" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="PO / Item" sortKey="po" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Material" sortKey="material" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Received" sortKey="received" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Status" sortKey="status" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Site" sortKey="site" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Date" sortKey="date" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
            </tr>
          </thead>
          <tbody>{filteredItems.map((item) => <WorklistTableRow key={item.worklist_id} item={item} isSelected={item.worklist_id === selectedId} onSelect={setSelectedId} onOpen={openCreateInvoiceTab} onRequestRestart={setRestartCandidate} onRequestRecovery={setRecoveryCandidate} onResync={resyncSap} onRequestLink={setLinkCandidate} restartingId={restartingId} recoveringId={recoveringId} resyncingId={resyncingId} linkingId={linkingId} />)}</tbody>
        </table>
      </div>
      {/* Mobile: compact card list */}
      <div className="divide-y divide-slate-200 bg-white lg:hidden" data-testid="due-invoicing-mobile-list">
        {filteredItems.map((item) => <WorklistCard key={item.worklist_id} item={item} isSelected={item.worklist_id === selectedId} onSelect={setSelectedId} onOpen={openCreateInvoiceTab} onRequestRestart={setRestartCandidate} onRequestRecovery={setRecoveryCandidate} onResync={resyncSap} onRequestLink={setLinkCandidate} restartingId={restartingId} recoveringId={recoveringId} resyncingId={resyncingId} linkingId={linkingId} />)}
      </div>
      {hasMore && !search && <div className="flex justify-center border-t border-slate-200 bg-white px-4 py-2.5">
        <Button
          variant="outline"
          size="sm"
          onClick={loadMore}
          disabled={loadingMore}
          className="h-7 gap-1.5 border-slate-300 bg-white text-[12px] font-normal text-slate-700 hover:bg-slate-50"
          data-testid="due-invoicing-load-more-button"
        >{loadingMore ? <Loader2 className="animate-spin" size={12} /> : null}{loadingMore ? "Loading…" : `Load more (${items.length} of ${total})`}</Button>
      </div>}
    </>}

    <AlertDialog open={Boolean(restartCandidate)} onOpenChange={(open) => { if (!open) setRestartCandidate(null); }}><AlertDialogContent data-testid={`restart-invoice-dialog-${restartCandidate?.worklist_id || "closed"}`}><AlertDialogHeader><AlertDialogTitle>{restartCandidate?.local_draft_status === "sap_draft_reconciliation_required" ? "Verify SAP void and restart?" : "Restart this local invoice?"}</AlertDialogTitle><AlertDialogDescription>{restartCandidate?.local_draft_status === "sap_draft_reconciliation_required" ? "This checks SAP for an active invoice on this GRN. Only if the void is confirmed will the old local draft be preserved as superseded and this receipt reopen for a fresh three-way match. It does not create, post, edit, or void anything in SAP." : "SAP will be checked again first. If no active invoice is linked to this GRN, the existing local drafts are preserved as superseded and you can start a fresh three-way match. This does not create, post, edit, or void anything in SAP."}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel data-testid={`restart-invoice-cancel-${restartCandidate?.worklist_id || "closed"}`}>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { const item = restartCandidate; setRestartCandidate(null); if (item) restartDraft(item); }} data-testid={`restart-invoice-confirm-${restartCandidate?.worklist_id || "closed"}`}>{restartCandidate?.local_draft_status === "sap_draft_reconciliation_required" ? "Verify void and restart" : "Recheck SAP and restart"}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    <AlertDialog open={Boolean(recoveryCandidate)} onOpenChange={(open) => { if (!open) setRecoveryCandidate(null); }}><AlertDialogContent data-testid={`recover-sap-draft-dialog-${recoveryCandidate?.worklist_id || "closed"}`}><AlertDialogHeader><AlertDialogTitle>Recover the existing SAP draft?</AlertDialogTitle><AlertDialogDescription>{recoveryCandidate?.local_draft_status === "sap_draft_reconciliation_required" ? "SAP may have created the draft before its response timed out. This searches SAP by the supplier reference and does not create, edit, post, or void an SAP document." : "This reads the existing SAP draft and restores the local review state only after SAP confirms its GRN links. It does not create, edit, post, or void an SAP document."}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel data-testid={`recover-sap-draft-cancel-${recoveryCandidate?.worklist_id || "closed"}`}>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { const item = recoveryCandidate; setRecoveryCandidate(null); if (item) recoverDraft(item); }} data-testid={`recover-sap-draft-confirm-${recoveryCandidate?.worklist_id || "closed"}`}>{recoveryCandidate?.local_draft_status === "sap_draft_reconciliation_required" ? "Read SAP and reconcile" : "Read SAP and recover"}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    <AlertDialog open={Boolean(linkCandidate)} onOpenChange={(open) => { if (!open) setLinkCandidate(null); }}><AlertDialogContent data-testid={`link-sap-posted-dialog-${linkCandidate?.worklist_id || "closed"}`}><AlertDialogHeader><AlertDialogTitle>Link SAP posted invoice and close locally?</AlertDialogTitle><AlertDialogDescription>This performs a read-only SAP OData probe (item-level verified) for every GRN on this receipt. If SAP confirms an active supplier invoice is item-level matched to the GRN, the worklist row is marked ``Already in SAP`` and a read-only audit record is stamped locally. No SAP data is created, posted, edited, or voided. GRNs with no SAP match are left untouched.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel data-testid={`link-sap-posted-cancel-${linkCandidate?.worklist_id || "closed"}`}>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { const item = linkCandidate; setLinkCandidate(null); if (item) linkSapPostedAndClose(item); }} data-testid={`link-sap-posted-confirm-${linkCandidate?.worklist_id || "closed"}`}>Verify SAP and link</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
  </section>;
};

/* ── Secondary row actions — compact text buttons, SAP style ───────── */

function SecondaryRowAction({ item, onRequestRestart, onRequestRecovery, restartingId, recoveringId, surface }) {
  const alreadyInSap = item.status === "fully_invoiced";
  const draft = item.status === "draft_in_progress";
  const needsVoidVerification = draft && item.local_sap_invoice_id && ["sap_draft_reconciliation_required", "sap_draft_grn_linked_review", "sap_draft_pending_review", "error"].includes(item.local_draft_status);
  const canRecover = draft && item.invoice_id && (
    (item.local_draft_status === "error" && item.local_sap_invoice_id)
    || (item.local_draft_status === "sap_draft_reconciliation_required" && !item.local_sap_invoice_id)
  );
  const canRestart = draft && !(item.supplier_invoice_ids || []).length && !(item.sap_invoice_refs || []).length;
  if (item.status === "three_way_exception" && item.three_way_exception_id) {
    // The Status pill above already shows the red "Exception" indicator —
    // this is a distinct, clickable shortcut to open it, not a second
    // status. Keep the label/icon visually different so it doesn't read
    // as a duplicate record.
    return <Button asChild size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px] font-normal text-slate-600 hover:bg-red-50 hover:text-red-800" data-testid={`view-exception-${surface}-${item.worklist_id}`}>
      <Link to={`/invoices/exceptions/${encodeURIComponent(item.three_way_exception_id)}`} onClick={(event) => event.stopPropagation()}>View exception <ChevronRight size={12} /></Link>
    </Button>;
  }
  if (needsVoidVerification) return <RestartDraftButton item={item} onRequestRestart={onRequestRestart} restartingId={restartingId} surface={surface} verifyVoid />;
  if (canRecover) return <div className="flex items-center gap-1">
    <RecoverDraftButton item={item} onRequestRecovery={onRequestRecovery} recoveringId={recoveringId} surface={surface} />
    {canRestart && <RestartDraftButton item={item} onRequestRestart={onRequestRestart} restartingId={restartingId} surface={surface} />}
  </div>;
  if (canRestart) return <RestartDraftButton item={item} onRequestRestart={onRequestRestart} restartingId={restartingId} surface={surface} />;
  // Default state — no corrective action required. The Status pill
  // itself tells the user what's going on; no placeholder needed.
  return null;
}

function RestartDraftButton({ item, onRequestRestart, restartingId, surface, verifyVoid = false }) {
  const busy = restartingId === item.worklist_id;
  const testId = verifyVoid ? `verify-sap-void-and-restart-${surface}-${item.worklist_id}` : `restart-local-invoice-${surface}-${item.worklist_id}`;
  return <Button size="sm" variant="ghost" disabled={busy} onClick={(event) => { event.stopPropagation(); onRequestRestart(item); }} className="h-6 gap-1 px-2 text-[11px] font-normal text-amber-800 hover:bg-amber-50 hover:text-amber-900" data-testid={testId}><RotateCcw size={12} className={busy ? "animate-spin" : ""} />{busy ? "Checking…" : verifyVoid ? "Verify void" : "Restart"}</Button>;
}

function RecoverDraftButton({ item, onRequestRecovery, recoveringId, surface }) {
  const busy = recoveringId === item.worklist_id;
  return <Button size="sm" variant="ghost" disabled={busy} onClick={(event) => { event.stopPropagation(); onRequestRecovery(item); }} className="h-6 gap-1 px-2 text-[11px] font-normal hover:bg-slate-50" style={{ color: SAP_BLUE }} data-testid={`recover-sap-draft-${surface}-${item.worklist_id}`}><FileCheck2 size={12} className={busy ? "animate-pulse" : ""} />{busy ? "Reading…" : "Recover"}</Button>;
}

function SapInvoiceCheckCell({ item, onResync, onRequestLink, onRequestRestart, onRequestRecovery, resyncingId, linkingId, restartingId, recoveringId, surface }) {
  const resyncBusy = resyncingId === item.worklist_id;
  const linkBusy = linkingId === item.worklist_id;
  const sapInvoiceIds = item.supplier_invoice_ids || [];
  const sapRefs = Object.values((item.sap_invoice_refs || sapInvoiceIds.map((invoiceId) => ({ invoice_id: invoiceId, status_label: "SAP invoice exists" }))).reduce((unique, reference) => ({ ...unique, [reference.invoice_id]: reference }), {}));
  const alreadyInSap = item.status === "fully_invoiced";
  const draft = item.status === "draft_in_progress" || item.status === "match_approved";
  const showLink = sapRefs.length > 0 && !alreadyInSap;
  const lifecycle = resolveLifecycle(item);
  const refreshTitle = draft
    ? "A local draft is being prepared — re-check available after post"
    : "Re-check SAP (read-only OData probe · no SAP writes)";
  return <div className="flex flex-col gap-0.5">
    <div className="flex items-center gap-1.5">
      <StatusPill tone={lifecycle.tone} icon={lifecycle.icon} label={lifecycle.label} testId={`status-pill-${item.worklist_id}`} />
      <button
        type="button"
        onClick={(event) => { event.stopPropagation(); if (!resyncBusy && !draft) onResync(item); }}
        disabled={resyncBusy || draft}
        title={refreshTitle}
        className="inline-flex h-4 w-4 items-center justify-center rounded text-slate-400 hover:bg-slate-200 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
        data-testid={`resync-sap-${surface}-${item.worklist_id}`}
        aria-label="Refresh SAP status"
      >
        <RefreshCw size={11} className={resyncBusy ? "animate-spin" : ""} />
      </button>
    </div>
    {sapRefs.map((reference) => <span key={reference.invoice_id} className="font-mono text-[11px] text-slate-500" data-testid={`sap-invoice-reference-${item.worklist_id}-${reference.invoice_id}`}>{reference.invoice_id}</span>)}
    {showLink && !draft && <button
      type="button"
      onClick={(event) => { event.stopPropagation(); onRequestLink(item); }}
      disabled={linkBusy}
      className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 hover:text-slate-800 disabled:opacity-50"
      data-testid={`link-sap-posted-${surface}-${item.worklist_id}`}
    >{linkBusy ? <Loader2 size={10} className="animate-spin" /> : <Link2 size={10} />}{linkBusy ? "Linking" : "Link & close"}</button>}
    {/* Row-level corrective actions (Recover / Restart / Verify void / Exception) */}
    <SecondaryRowAction item={item} onRequestRestart={onRequestRestart} onRequestRecovery={onRequestRecovery} restartingId={restartingId} recoveringId={recoveringId} surface={surface} />
  </div>;
}

function MaterialSummary({ item }) {
  const materials = (item.po_items || []).map((entry) => entry.material_code).filter(Boolean);
  const unique = Array.from(new Set(materials));
  if (!unique.length) return <span className="text-slate-400">—</span>;
  if (unique.length === 1) return <span className="font-mono text-slate-700" data-testid={`due-material-${item.worklist_id}`}>{unique[0]}</span>;
  return <span className="font-mono text-slate-700" title={unique.join(", ")} data-testid={`due-material-${item.worklist_id}`}>{unique[0]}<span className="ml-1 text-slate-500">+{unique.length - 1}</span></span>;
}

function RowBadges({ item }) {
  const grouped = Boolean(item.grouped) && Number(item.group_size || 0) > 1;
  const fifoPriority = (item.fifo_unblocks || []).length > 0;
  // Blocked / Exception / Draft are surfaced by the lifecycle pill in the
  // Status column; this row adds the grouping meta (orthogonal — a single
  // invoice may span multiple GRNs) plus the FIFO-priority call-out — the
  // one clear, scannable signal for "start the chain here" instead of
  // making finance read every blocked row's text to find the root.
  if (!grouped && !fifoPriority) return null;
  return <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
    {fifoPriority && <StatusPill tone="success" icon={ArrowRightCircle} label="Invoice first" testId={`due-fifo-priority-badge-${item.worklist_id}`} />}
    {grouped && <StatusPill tone="info" label={`${item.group_size} GRNs`} testId={`due-grouped-badge-${item.worklist_id}`} />}
  </div>;
}

function formatCompactDate(iso) {
  return fmtDate(iso);
}

function WorklistTableRow({ item, isSelected, onSelect, onOpen, onRequestRestart, onRequestRecovery, onResync, onRequestLink, restartingId, recoveringId, resyncingId, linkingId }) {
  const toolbar = toolbarEligibility(item);
  const blockedByEarlierGrn = Boolean(item.blocked_by_earlier_grn);
  const blockers = item.open_grn_blockers || [];
  const rowStyle = isSelected
    ? { backgroundColor: SAP_SELECTED_BG, borderLeftColor: SAP_BLUE, borderLeftWidth: 3 }
    : { backgroundColor: "#FFFFFF", borderLeftColor: "transparent", borderLeftWidth: 3 };
  const dateIso = item.materials_hub_grn_created_at || item.source_updated_at || item.reconciled_at;
  return <tr
    className="cursor-pointer border-b border-slate-300 hover:bg-[#EAEFF2]"
    style={rowStyle}
    data-testid={`due-invoicing-row-${item.worklist_id}`}
    aria-selected={isSelected}
    onClick={() => onSelect(item.worklist_id)}
    onDoubleClick={() => { if (toolbar.eligible) onOpen(item); }}
  >
    <td className="px-4 py-3 align-middle" data-testid={`due-shipment-${item.worklist_id}`}>
      <span className="font-mono text-[13px] font-medium text-slate-800">{item.shipment_code || "—"}</span>
      <RowBadges item={item} />
    </td>
    <td className="px-4 py-3 align-middle text-slate-700" data-testid={`due-supplier-${item.worklist_id}`}>{item.vendor_name || "—"}</td>
    <td className="px-4 py-3 align-middle">
      <div className="font-mono text-[12px] text-slate-700" data-testid={`due-notification-${item.worklist_id}`}>{notificationLabel(item)}</div>
      <div className="font-mono text-[12px] text-slate-500" data-testid={`due-grn-${item.worklist_id}`}>GRN {grnLabel(item)}</div>
      {blockedByEarlierGrn && (blockers.length > 0
        ? <div className="mt-0.5 text-[11px] text-amber-800" data-testid={`due-blocked-reason-${item.worklist_id}`}>Invoice {blockers.map((b) => b.shipment_code).join(", ")} first</div>
        : (item.blocked_reasons || [])[0] && <div className="mt-0.5 text-[11px] text-amber-800" data-testid={`due-blocked-reason-${item.worklist_id}`}>{item.blocked_reasons[0]}</div>)}
      {(item.fifo_unblocks || []).length > 0 && <div className="mt-0.5 text-[11px] font-medium" style={{ color: "#107E3E" }} data-testid={`due-fifo-unblocks-${item.worklist_id}`}>Unblocks {item.fifo_unblocks.join(", ")} once invoiced</div>}
    </td>
    <td className="px-4 py-3 align-middle font-mono text-[12px] text-slate-600" data-testid={`due-po-${item.worklist_id}`}>{(item.po_items || []).length ? (item.po_items || []).map((entry) => `${entry.po_number}/${entry.po_item_number}`).join(", ") : (item.po_numbers || []).join(", ") || "—"}</td>
    <td className="px-4 py-3 align-middle"><MaterialSummary item={item} /></td>
    <td className="px-4 py-3 align-middle font-mono text-[12px] text-slate-700" data-testid={`due-quantity-${item.worklist_id}`}>{receivedLabel(item)}</td>
    <td className="px-4 py-3 align-middle" data-testid={`due-sap-validation-${item.worklist_id}`}>
      <SapInvoiceCheckCell
        item={item}
        onResync={onResync}
        onRequestLink={onRequestLink}
        onRequestRestart={onRequestRestart}
        onRequestRecovery={onRequestRecovery}
        resyncingId={resyncingId}
        linkingId={linkingId}
        restartingId={restartingId}
        recoveringId={recoveringId}
        surface="table"
      />
    </td>
    <td className="px-4 py-3 align-middle text-[13px] text-slate-700" data-testid={`due-site-${item.worklist_id}`}>{item.site || "—"}{item.buyer_entity_name && <span className="block text-[11px] text-slate-400">{item.buyer_entity_name}</span>}</td>
    <td className="px-4 py-3 align-middle whitespace-nowrap text-right text-[12px] text-slate-600" title={dateIso || ""} data-testid={`due-date-${item.worklist_id}`}>{formatCompactDate(dateIso)}</td>
  </tr>;
}

function WorklistCard({ item, isSelected, onSelect, onOpen, onRequestRestart, onRequestRecovery, onResync, onRequestLink, restartingId, recoveringId, resyncingId, linkingId }) {
  const toolbar = toolbarEligibility(item);
  const blockedByEarlierGrn = Boolean(item.blocked_by_earlier_grn);
  const blockers = item.open_grn_blockers || [];
  const dateIso = item.materials_hub_grn_created_at || item.source_updated_at || item.reconciled_at;
  const style = isSelected
    ? { backgroundColor: SAP_SELECTED_BG, borderLeft: `3px solid ${SAP_BLUE}` }
    : { backgroundColor: "#FFFFFF", borderLeft: "3px solid transparent" };
  return <article
    className="cursor-pointer px-4 py-2.5"
    style={style}
    data-testid={`due-invoicing-mobile-card-${item.worklist_id}`}
    aria-selected={isSelected}
    onClick={() => onSelect(item.worklist_id)}
    onDoubleClick={() => { if (toolbar.eligible) onOpen(item); }}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[13px] font-semibold text-slate-800" data-testid={`due-mobile-shipment-${item.worklist_id}`}>{item.shipment_code || "—"}</p>
        <RowBadges item={item} />
        <p className="mt-0.5 text-[12px] text-slate-700" data-testid={`due-mobile-supplier-${item.worklist_id}`}>{item.vendor_name || "—"}</p>
      </div>
      <span className="shrink-0 text-[11px] text-slate-500" data-testid={`due-mobile-date-${item.worklist_id}`}>{formatCompactDate(dateIso)}</span>
    </div>
    <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[11px] text-slate-600">
      <span data-testid={`due-mobile-reference-${item.worklist_id}`}>GRN {grnLabel(item)}</span>
      <span data-testid={`due-mobile-po-${item.worklist_id}`}>PO {(item.po_items || []).map((entry) => `${entry.po_number}/${entry.po_item_number}`).join(", ") || (item.po_numbers || []).join(", ") || "—"}</span>
      <span data-testid={`due-mobile-quantity-${item.worklist_id}`}>{receivedLabel(item)}</span>
      <span data-testid={`due-mobile-material-${item.worklist_id}`}><MaterialSummary item={item} /></span>
      <span data-testid={`due-mobile-site-${item.worklist_id}`}>Site {item.site || "—"}{item.buyer_entity_name ? ` (${item.buyer_entity_name})` : ""}</span>
    </div>
    {blockedByEarlierGrn && (blockers.length > 0
      ? <p className="mt-1 text-[11px] text-amber-800" data-testid={`due-mobile-blocked-reason-${item.worklist_id}`}>Invoice {blockers.map((b) => b.shipment_code).join(", ")} first</p>
      : (item.blocked_reasons || [])[0] && <p className="mt-1 text-[11px] text-amber-800" data-testid={`due-mobile-blocked-reason-${item.worklist_id}`}>{item.blocked_reasons[0]}</p>)}
    {(item.fifo_unblocks || []).length > 0 && <p className="mt-1 text-[11px] font-medium" style={{ color: "#107E3E" }} data-testid={`due-mobile-fifo-unblocks-${item.worklist_id}`}>Unblocks {item.fifo_unblocks.join(", ")} once invoiced</p>}
    <div className="mt-1.5">
      <SapInvoiceCheckCell
        item={item}
        onResync={onResync}
        onRequestLink={onRequestLink}
        onRequestRestart={onRequestRestart}
        onRequestRecovery={onRequestRecovery}
        resyncingId={resyncingId}
        linkingId={linkingId}
        restartingId={restartingId}
        recoveringId={recoveringId}
        surface="mobile"
      />
    </div>
  </article>;
}
